import { makeObservable, observable, action } from 'mobx'

import FeatureFlag from './models/FeatureFlag'
import { getUserRoles } from '../../../utils/helpers'

class Store {
    appStore

    @observable loading = true

    @observable view = 'index'

    @observable query = ''

    @observable scopeFilter = 'all'

    @observable stateFilter = 'all'

    @observable openGateKey = null

    @observable flags = []

    @observable flag = null

    @observable catalog = []

    constructor(appStore) {
        makeObservable(this)
        this.appStore = appStore
        this.userRoles = Object.entries(getUserRoles()).map(([k, v]) => [Number(k), v])
    }

    loadFlags = async () => {
        try {
            const flags = await this.appStore.http.get('/intranet/featureflag')
            this.flags = flags
        } catch (e) {
            console.error(e)
        }
    }

    init = async () => {
        this.catalog = []

        try {
            const catalog = await this.appStore.http.get('/intranet/featureflag/tags')
            this.catalog = catalog
        } catch (e) {
            console.error(e)
        }

        this.loading = false
    }

    logicOptions = (scope) => (
        [
            {
                label: 'Ignore',
                value: 'ignore',
                hint: 'Skip this check. It always passes.',
                show: () => true,
            },
            {
                label: 'Match any',
                value: 'OR',
                hint: 'Passes if any listed value matches.',
                show: () => true,
            },
            {
                label: 'All',
                value: 'all',
                hint: 'Passes only if every listed value matches.',
                show: () => scope === 'tags',
            },
        ].filter(({ show }) => show())
    )

    getLogicLabel = (logic, scope) => (
        this.logicOptions(scope)?.find(({ value }) => value === logic)?.label ?? logic
    )

    @action
    setQuery = (e) => { this.query = e.target.value }

    @action
    setScopeFilter = (value) => { this.scopeFilter = value }

    @action
    setStateFilter = (value) => { this.stateFilter = value }

    @action
    openFlag = async (row) => {
        const flag = await this.appStore.http.get(`/intranet/featureflag/${row.id}`)
        this.flag = new FeatureFlag(flag)
        this.view = 'edit'
    }

    @action
    newFlag = () => {
        this.flag = new FeatureFlag({ scope: 'global' })
        this.view = 'edit'
    }

    @action
    backToIndex = () => {
        this.view = 'index'
        this.flag = null
        this.openGateKey = null
    }

    @action
    setScope = (value) => {
        this.flag.scope = value
        if (value === 'global' && String(this.openGateKey).indexOf('survey') === 0) {
            this.openGateKey = null
        }
    }

    @action
    toggleActive = () => { this.flag.active = !this.flag.active }

    @action
    updateLabel = (e) => { this.flag.label = e.target.value }

    @action
    updateName = (e) => { this.flag.name = e.target.value }

    @action
    updateDescription = (e) => { this.flag.description = e.target.value }

    scopeLabel = {
        role: 'Role',
        addons: 'Role addons',
    }

    updateFlag = async () => {
        try {
            await this.appStore.http.put(`/intranet/featureflag/${this.flag.id}`, JSON.stringify(this.flag))
            this.appStore.addNotification({ body: 'Feature flag updated', level: 'success' })
            this.backToIndex()
        } catch (e) {
            console.error(e)
            this.appStore.addNotification({ body: 'Error updating feature flag', level: 'error' })
        }
    }

    createFlag = async () => {
        try {
            await this.appStore.http.post('/intranet/featureflag', JSON.stringify(this.flag))
            this.appStore.addNotification({ body: 'Feature flag created', level: 'success' })
            this.backToIndex()
        } catch (e) {
            console.error(e)
            this.appStore.addNotification({ body: 'Error creating feature flag', level: 'error' })
        }
    }

    get filteredRows() {
        const q = this.query.toLowerCase()
        return this.flags.filter((row) => {
            if (q && `${row.name} ${row.label}`.toLowerCase().indexOf(q) < 0) return false
            if (this.scopeFilter !== 'all' && row.scope !== this.scopeFilter) return false
            if (this.stateFilter === 'live' && !row.active) return false
            if (this.stateFilter === 'suspended' && row.active) return false
            return true
        })
    }

    gateError = (gate) => {
        if (gate.logic === 'ignore') return null
        return gate.options.length ? null : 'Empty list'
    }

    featureFlagErrors = (flag) => (
        ['role', 'addons']
            .map((scope) => [scope, this.gateError(flag.data[scope])])
            .filter(([, error]) => !!error)
            .map(([scope, error]) => `${this.scopeLabel[scope]}: ${error}`)
    )
}

export default Store
